import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFillApplicationActionProps } from 'shared/hooks/useFillApplicationAction';
import { IAuthorizedModal, ICalculatorMcfValues, ILandingState, TCalculatorMcfErrorsItem } from 'shared/types/features';
import { IShortOfferTermsModalData } from 'shared/types/features/landing';
import { EFillApplicationStickyButtonType } from '../../containers/stickyButtons/FillApplicationButton/types';
import { IOfferForAnalytic } from '../../utils/tracking/types';
import { ISubmittedApplication } from '../../utils/resolveActualApplication';
import { IRow } from '../../repositories/mortgage-broker-calculator/entities/modal/Row';

export const initialState: ILandingState = {
  isModalOpen: false,
  isCianAuthorizedModalOpen: false,
  modalWindowContent: null,
  modalWindowRedirectParams: null,
  isTermsModalOpen: false,
  termsModalData: null,
  authorizedModal: {
    isOpen: false,
  },
  calculatorDefaultValues: undefined,
  calculatorValues: {} as ICalculatorMcfValues,
  calculatorErrors: [],
  activeMasterButton: null,
  offersLength: null,
  actionUrl: null,
  financePlatformEnabled: undefined,
  prefetchedOffersData: null,
  prefetchedOffersRequest: null,
  seoData: null,
  seoMarkedList: null,
  seoBreadcrumbs: null,
  seoQuickLinks: null,
  seoCrossLinks: null,
  unfinishedAnketaModal: { isOpen: false, path: null, place: null },
  applicationStatusModal: { isOpen: false, application: null, place: null },
  offersForAnalytic: null,
  shortFormGroup: null,
};

/* istanbul ignore next */
export const landingSlice = createSlice({
  initialState,
  name: 'landing',
  reducers: {
    setModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setCianAuthorizedModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCianAuthorizedModalOpen = action.payload;
    },
    setAuthorizedModal: (state, action: PayloadAction<IAuthorizedModal>) => {
      state.authorizedModal.isOpen = action.payload.isOpen;
      state.authorizedModal.pathForRedirect = action.payload.pathForRedirect;
      state.authorizedModal.bankName = action.payload.bankName;
      state.authorizedModal.typeForRedirect = action.payload.typeForRedirect;
      state.authorizedModal.targetElementForRedirect = action.payload.targetElementForRedirect;
    },
    setCalculatorValues: (state, action: PayloadAction<ICalculatorMcfValues>) => {
      state.calculatorValues = action.payload;
    },
    setCalculatorErrors: (state, action: PayloadAction<TCalculatorMcfErrorsItem[]>) => {
      state.calculatorErrors = action.payload;
    },
    resetCalculatorErrors: state => {
      state.calculatorErrors = [];
    },
    resetCalculatorValues: state => {
      state.calculatorValues = {} as ICalculatorMcfValues;
    },
    setModalContent: (state, action: PayloadAction<IRow[] | null>) => {
      state.modalWindowContent = action.payload;
    },
    setModalWindowOfferParams: (state, action: PayloadAction<IFillApplicationActionProps | null>) => {
      state.modalWindowRedirectParams = action.payload;
    },
    setTermsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTermsModalOpen = action.payload;
    },
    setTermsModalData: (state, action: PayloadAction<IShortOfferTermsModalData | null>) => {
      state.termsModalData = action.payload;
    },
    setActiveMasterButton: (state, action: PayloadAction<EFillApplicationStickyButtonType | 'showOffers' | null>) => {
      state.activeMasterButton = action.payload;
    },
    setOffersLength: (state, action: PayloadAction<number | null>) => {
      state.offersLength = action.payload;
    },
    setActionUrl: (state, action: PayloadAction<string>) => {
      state.actionUrl = action.payload;
    },
    setFinancePlatformEnabled: (state, action: PayloadAction<boolean | undefined>) => {
      state.financePlatformEnabled = action.payload;
    },
    openUnfinishedAnketaModal: (state, action: PayloadAction<{ path: string; place: number | null }>) => {
      state.unfinishedAnketaModal = { isOpen: true, path: action.payload.path, place: action.payload.place };
    },
    closeUnfinishedAnketaModal: state => {
      state.unfinishedAnketaModal = { isOpen: false, path: null, place: null };
    },
    openApplicationStatusModal: (
      state,
      action: PayloadAction<{ application: ISubmittedApplication; place: number | null }>,
    ) => {
      state.applicationStatusModal = {
        isOpen: true,
        application: action.payload.application,
        place: action.payload.place,
      };
    },
    closeApplicationStatusModal: state => {
      state.applicationStatusModal = { isOpen: false, application: null, place: null };
    },
    setOffersForAnalytic: (state, action: PayloadAction<IOfferForAnalytic[] | null>) => {
      state.offersForAnalytic = action.payload;
    },
    setShortFormGroup: (state, action: PayloadAction<string>) => {
      state.shortFormGroup = action.payload;
    },
  },
});

export const {
  setModalOpen,
  setCianAuthorizedModalOpen,
  setAuthorizedModal,
  resetCalculatorValues,
  resetCalculatorErrors,
  setCalculatorErrors,
  setCalculatorValues,
  setModalContent,
  setModalWindowOfferParams,
  setTermsModalOpen,
  setTermsModalData,
  setActiveMasterButton,
  setOffersLength,
  setActionUrl,
  setFinancePlatformEnabled,
  openUnfinishedAnketaModal,
  closeUnfinishedAnketaModal,
  openApplicationStatusModal,
  closeApplicationStatusModal,
  setOffersForAnalytic,
  setShortFormGroup,
} = landingSlice.actions;
